/**
 * Ghost of a filled influences gallery: five genres, artists wrapped onto
 * two rows. Photos come from the cached first artists of the popular genres.
 */

export const PREVIEW_GENRES = ['Indie Rock', 'Neo Soul', 'Hip Hop', 'Jazz', 'Electronic'] as const;

const PREVIEW_NAMES = [
    'June Calder',
    'The North Atlas',
    'Vesper',
    'Milo Hart',
    'Sable Quinn',
    'Golda Reyes',
    'Ivo March',
    'Petra Solano',
    'Hanif Cole',
    'Willa Brooks',
    'Omar Vale',
    'Nia Okonkwo',
    'Felix Drum',
    'Ada Marlowe',
    'Rio Santana',
    'Cleo Voss',
    'Jonah Pike',
    'Mara Ellison',
    'Theo Lang',
    'Yara Bennett',
    'Cass Ortega',
    'Leif Navarro',
    'Noor Hale',
    'Ezra Bloom',
    'Indira Shah',
    'Pax Moreau',
    'Helena Cho',
    'Sid Harlow',
    'Amina Diallo',
    'Kit Serrano',
    'Bea Lindgren',
    'Rafi Mendes',
];

export interface CatalogArtist {
    id: string;
    name: string;
    image: string;
}

export interface CatalogGroup {
    genre: string;
    artists: CatalogArtist[];
}

const PREVIEW_GENRE_COUNT = 5;
const PREVIEW_ARTISTS_PER_GENRE = 6;

export interface PreviewArtist {
    id: string;
    name: string;
    displayText: string;
    image: string;
    genre: string;
}

export interface InfluencePreview {
    artists: PreviewArtist[];
    looseGenres: string[];
    genreOrder: string[];
}

const PORTRAIT_WIDTH = 96;
const PORTRAIT_GAP = 8;

/**
 * One track per portrait that fits. Every full row uses this same track, so a
 * long name or subgenre cannot make one row hold a different count.
 * 6rem matches the portrait cell (Tailwind w-24) at the default root size.
 */
export function influenceRowTemplate() {
    return 'repeat(auto-fill, 6rem)';
}

function splitEvenly(total: number, parts: number) {
    const base = Math.floor(total / parts);
    const extra = total % parts;
    return Array.from({ length: parts }, (_, index) => base + (index < extra ? 1 : 0));
}

/**
 * How many fixed-width portraits fit on one row.
 * Returns 0 when the row has not been measured yet.
 */
export function portraitsPerRow(contentWidth: number, itemWidth = PORTRAIT_WIDTH, gap = PORTRAIT_GAP) {
    if (!(contentWidth > 0) || !(itemWidth > 0) || !(gap >= 0)) return 0;
    return Math.max(1, Math.floor((contentWidth + gap) / (itemWidth + gap)));
}

function readGap(value: string) {
    const parsed = Number.parseFloat(value);
    if (!Number.isFinite(parsed)) return PORTRAIT_GAP;
    if (value.trim().endsWith('rem')) return parsed * 16;
    return parsed;
}

/**
 * Column count for the gallery currently laid out in `frame`.
 * Uses the fixed track width, not the measured cell, so text inside a
 * portrait cannot change how many columns the next render asks for.
 */
export function previewColumns(frame: HTMLElement) {
    const list = frame.querySelector('ul');
    const width = list instanceof HTMLElement ? list.clientWidth : frame.clientWidth;
    const gap = list instanceof HTMLElement ? readGap(getComputedStyle(list).columnGap) : PORTRAIT_GAP;
    return portraitsPerRow(width, PORTRAIT_WIDTH, gap);
}

function mulberry32(seed: number) {
    let state = seed >>> 0;
    return () => {
        state = (state + 0x6D2B79F5) >>> 0;
        let value = state;
        value = Math.imul(value ^ (value >>> 15), value | 1);
        value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
        return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
    };
}

function shuffled<T>(items: T[], random: () => number) {
    const copy = [...items];
    for (let index = copy.length - 1; index > 0; index -= 1) {
        const swap = Math.floor(random() * (index + 1));
        const current = copy[index];
        copy[index] = copy[swap];
        copy[swap] = current;
    }
    return copy;
}

/** Spread slots across genres, one at a time, without passing a genre's cap. */
export function distributePreviewSlots(slots: number, caps: number[]) {
    const counts = caps.map(() => 0);
    let left = Math.max(0, Math.floor(slots));
    while (left > 0) {
        let placed = false;
        for (let index = 0; index < caps.length && left > 0; index += 1) {
            if (counts[index] >= caps[index]) continue;
            counts[index] += 1;
            left -= 1;
            placed = true;
        }
        if (!placed) break;
    }
    return counts;
}

function previewFromCatalog(perRow: number, catalog: CatalogGroup[], seed: number): InfluencePreview {
    const random = mulberry32(seed);
    const requireImages = catalog.some((group) => group.artists.some((artist) => artist.image));
    const usable = shuffled(catalog, random).filter((group) => (
        group.genre && group.artists.some((artist) => artist.id && (!requireImages || artist.image))
    )).slice(0, PREVIEW_GENRE_COUNT);
    const decks = usable.map((group) => ({
        genre: group.genre,
        artists: shuffled(group.artists, random),
    }));
    const caps = decks.map((deck) => Math.min(
        PREVIEW_ARTISTS_PER_GENRE,
        deck.artists.filter((artist) => artist.id && (!requireImages || artist.image)).length,
    ));
    const counts = distributePreviewSlots(Math.max(1, Math.floor(perRow)) * 2, caps);
    const used = new Set<string>();
    const artists: PreviewArtist[] = [];
    decks.forEach((deck, index) => {
        let taken = 0;
        deck.artists.forEach((artist) => {
            if (taken >= counts[index] || !artist.id || used.has(artist.id)) return;
            if (requireImages && !artist.image) return;
            used.add(artist.id);
            taken += 1;
            artists.push({
                id: artist.id,
                name: artist.name,
                displayText: artist.name,
                image: artist.image || '',
                genre: deck.genre,
            });
        });
    });
    return {
        artists,
        looseGenres: [],
        genreOrder: decks.map((deck) => deck.genre).filter((genre) => artists.some((artist) => artist.genre === genre)),
    };
}

/** Grey circles while the cached photos have not arrived yet. */
export function placeholderCatalog(): CatalogGroup[] {
    return ['Pop', 'Hip Hop', 'Rock', 'R&B', 'Electronic'].map((genre) => ({
        genre,
        artists: Array.from({ length: PREVIEW_ARTISTS_PER_GENRE }, (_, index) => ({
            id: `placeholder-${genre}-${index}`,
            name: 'Artist',
            image: '',
        })),
    }));
}

/**
 * First row is two genre groups, second row is three, filling the row exactly
 * so nothing wraps onto a third line. Narrow rows keep the extra genres as chips.
 */
export function buildInfluencePreview(perRow: number, catalog?: CatalogGroup[] | null, seed = 1): InfluencePreview {
    if (catalog?.length) return previewFromCatalog(perRow, catalog, seed);
    const row = Math.max(1, Math.floor(perRow));
    const slots = row * 2;
    const grouped = row >= 3 ? [...splitEvenly(row, 2), ...splitEvenly(row, 3)] : Array.from({ length: slots }, () => 1);
    const artists: PreviewArtist[] = [];
    grouped.forEach((count, genreIndex) => {
        const genre = PREVIEW_GENRES[genreIndex];
        for (let item = 0; item < count; item += 1) {
            const index = artists.length;
            const name = PREVIEW_NAMES[index % PREVIEW_NAMES.length];
            artists.push({
                id: `preview-${index}`,
                name,
                displayText: name,
                image: previewPortrait(index),
                genre,
            });
        }
    });
    return {
        artists,
        looseGenres: row >= 3 ? [] : [...PREVIEW_GENRES.slice(slots)],
        genreOrder: [...PREVIEW_GENRES],
    };
}

function hue(index: number, shift: number) {
    return (index * 53 + shift) % 360;
}

/**
 * Soft color fields, not a drawn face. The gallery blur turns them into
 * the out-of-focus photos a filled profile would show.
 */
export function previewPortrait(index: number) {
    const base = hue(index, 18);
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80"><defs><radialGradient id="g" cx="${28 + (index % 4) * 8}%" cy="${24 + (index % 3) * 10}%"><stop offset="0%" stop-color="hsl(${base} 42% 72%)"/><stop offset="55%" stop-color="hsl(${hue(index, 40)} 48% 42%)"/><stop offset="100%" stop-color="hsl(${hue(index, 160)} 40% 24%)"/></radialGradient></defs><rect width="80" height="80" fill="url(#g)"/><circle cx="${18 + (index % 5) * 8}" cy="${22 + (index % 4) * 6}" r="${16 + (index % 3) * 4}" fill="hsl(${hue(index, 200)} 36% ${62 + (index % 3) * 6}%)" opacity="0.8"/><circle cx="${48 + (index % 3) * 6}" cy="${52 + (index % 4) * 4}" r="22" fill="hsl(${hue(index, 90)} 44% 32%)" opacity="0.75"/></svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}
